import { useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { t } from "@/i18n-js/instance";
import { useLazyMobileAppSettings } from "@circle-react/components/SettingsApp/MobileApp/hooks/useLazyMobileAppSettings";
import type { ApiError } from "@circle-react/config/CustomErrors";
import {
  reactQueryGet,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { MobileHomeSegment } from "@circle-react/types/MobileHomeSegment";
import type {
  BaseBlock,
  Pages,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { createHomeTabsRootBlockFromMobileSegments } from "../components/Blocks/HomeTabsBlock/HomeTabsRootBlocksDefaultData";
import {
  Screen1RootBlock,
  Screen2RootBlock,
} from "../components/Blocks/Onboarding/ScreensRootBlocksDefaultData";
import type { HomeTabsMobileSegmentAsMetaType } from "../types/HomeTabs.types";
import type { PagesStatus } from "../types/types";
import { pickAndSortPagesByMetaTypeAndOrder } from "../utils/pages.utils";

export const BRANDED_APPS_PAGES_QUERY_KEY = "branded-apps-pages";

type PageResult = Record<
  string,
  { content: BaseBlock; inline_attachments: Record<string, unknown>[] }
>;

export interface BrandedAppsPagesResponse {
  admin_preview_enabled: true;
  enabled: boolean;
  home_tabs_pages: PageResult;
  onboarding_pages: PageResult;
  status: PagesStatus;
  version: number;
}

const DEFAULT_ONBOARDING_PAGES = {
  [Screen1RootBlock.id]: Screen1RootBlock,
  [Screen2RootBlock.id]: Screen2RootBlock,
};

const extractContentAsPage = (pagesResults: PageResult) =>
  Object.entries(pagesResults).reduce<Pages>(
    (acc, [id, { content, inline_attachments }]) => ({
      ...acc,
      [id]: {
        ...content,
        inline_attachments,
      },
    }),
    {},
  );

const mapMobileSegmentToHomeTabsMeta = (
  mobileSegment: MobileHomeSegment,
  order: number,
): HomeTabsMobileSegmentAsMetaType => ({
  id: uuidv4(),
  isInNav: mobileSegment.available,
  custom_name: mobileSegment.custom_name,
  kind: mobileSegment.kind,
  isDefaultPage: true,
  order,
  access_groups: [],
});

const createPagesFromMobileSegments = (
  mobileHomeSegments: MobileHomeSegment[],
): Pages => {
  if (!mobileHomeSegments?.length) return {};

  let inNavOrder = 0;
  let notInNavOrder = 0;

  const allSegmentsAsMeta = mobileHomeSegments.map(segment => {
    let order = 0;

    if (segment.available) {
      order = inNavOrder;
      inNavOrder++;
    } else {
      order = notInNavOrder;
      notInNavOrder++;
    }

    return mapMobileSegmentToHomeTabsMeta(segment, order);
  });

  return allSegmentsAsMeta.reduce<Pages>(
    (acc, segmentAsMeta, _, allSegmentsAsMeta) => {
      const block = createHomeTabsRootBlockFromMobileSegments({
        homeTabMeta: segmentAsMeta,
        homeTabsMetaFromAllSegments: allSegmentsAsMeta,
      });
      return { ...acc, [block.id]: block };
    },
    {},
  );
};

export interface UsePagesDataProps {
  enabled?: boolean;
  isFetchEnabled?: boolean;
  status?: PagesStatus;
}

export const usePagesData = ({
  status,
  enabled,
  isFetchEnabled = true,
}: UsePagesDataProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [fetchPageProps, setFetchPageProps] = useState<UsePagesDataProps>({
    status,
    enabled,
    isFetchEnabled,
  });

  const {
    mobileHomeSegments,
    isLoading: isLoadingSegments,
    fetchSettings,
  } = useLazyMobileAppSettings();

  // update state when props changes
  useEffect(() => {
    setFetchPageProps(prev => ({ ...prev, status, isFetchEnabled, enabled }));
  }, [enabled, isFetchEnabled, status]);

  const { data, isLoading, refetch } = useQuery<
    void,
    ApiError,
    BrandedAppsPagesResponse
  >({
    queryKey: [BRANDED_APPS_PAGES_QUERY_KEY],
    queryFn: () =>
      reactQueryGet(
        internalApi.brandedAppsBuilderUI.brandedAppsBuilderPages.get<UsePagesDataProps>(
          { status: fetchPageProps.status },
        ),
      ),
    enabled: fetchPageProps.isFetchEnabled,
    onSuccess: data => {
      if (isEmpty(data.home_tabs_pages)) {
        void fetchSettings();
      }
    },
  });

  const refetchPagesData = ({ status }: UsePagesDataProps) => {
    setFetchPageProps(prev => ({ ...prev, status }));
    void refetch();
  };

  const { mutateAsync: savePages } = useMutation<
    BrandedAppsPagesResponse,
    ApiError,
    { pages: Pages; status: PagesStatus }
  >({
    mutationFn: ({ pages, status }) => {
      const homeTabsPages: Pages = pickAndSortPagesByMetaTypeAndOrder({
        pages,
        type: "hometabs",
      });
      const onboardingPages: Pages = pickAndSortPagesByMetaTypeAndOrder({
        pages,
        type: "onboarding",
      });

      delete onboardingPages.inline_attachments;
      delete homeTabsPages.inline_attachments;

      const payload = {
        home_tabs_pages: homeTabsPages,
        onboarding_pages: onboardingPages,
        version: 1,
        status: status,
      };

      return reactQueryPost(
        internalApi.brandedAppsBuilderUI.brandedAppsBuilderPages.upsert(),
        payload,
      );
    },
    onSuccess: data => {
      queryClient.setQueryData([BRANDED_APPS_PAGES_QUERY_KEY], data);
      toast.success(t("branded_apps_builder_ui.save.success"));
    },
    onError: (error: { message: string }) => {
      toast.error(t("branded_apps_builder_ui.save.error"));
      console.error(error);
    },
  });

  const pagesMeta = useMemo(() => {
    if (!data) return {};

    return { status: data.status };
  }, [data]);

  const onboardingPages = useMemo(() => {
    if (!data) return DEFAULT_ONBOARDING_PAGES;
    if (isEmpty(data.onboarding_pages)) return DEFAULT_ONBOARDING_PAGES;

    return extractContentAsPage(data.onboarding_pages);
  }, [data]);

  const homeTabsPages = useMemo(() => {
    if (!data) return {};

    const hasExistingPages = !isEmpty(data.home_tabs_pages);

    if (hasExistingPages) {
      return extractContentAsPage(data.home_tabs_pages);
    }

    const hasMobileSegments = mobileHomeSegments?.length > 0;
    if (hasMobileSegments) {
      return createPagesFromMobileSegments(mobileHomeSegments);
    }

    return {};
  }, [data, mobileHomeSegments]);

  const isAdminPreviewEnabled = useMemo(
    () => !!data?.admin_preview_enabled,
    [data?.admin_preview_enabled],
  );

  const { mutateAsync: changeAdminPreviewEnabled } = useMutation<
    BrandedAppsPagesResponse,
    ApiError,
    boolean
  >({
    mutationFn: (isAdminPreviewEnabled: boolean) => {
      const payload = {
        admin_preview_enabled: !!isAdminPreviewEnabled,
      };

      return reactQueryPost(
        internalApi.brandedAppsBuilderUI.brandedAppsBuilderPages.upsert(),
        payload,
      );
    },
    onSuccess: response => {
      queryClient.setQueryData<BrandedAppsPagesResponse>(
        [BRANDED_APPS_PAGES_QUERY_KEY, fetchPageProps],
        (oldData: BrandedAppsPagesResponse | undefined) => {
          if (!oldData) return response;
          return {
            ...oldData,
            admin_preview_enabled: response.admin_preview_enabled,
            enabled: response.enabled,
          };
        },
      );
      toast.success(t("branded_apps_builder_ui.save.success"));
    },
    onError: (error: { message: string }) => {
      toast.error(t("branded_apps_builder_ui.save.error"));
      console.error(error);
    },
  });

  return {
    onboardingPages,
    homeTabsPages,
    savePages,
    isAdminPreviewEnabled,
    changeAdminPreviewEnabled,
    refetchPagesData,
    pagesMeta,
    isLoading: isLoading || isLoadingSegments,
  };
};
